import { useMemo, useState } from 'react';

import type { InitialBooleanState } from './types';

/**
 * Custom React hook for managing a boolean state with convenient callbacks.
 *
 * @param initialState - The initial boolean value for the state. Defaults to `false`.
 * @returns A tuple containing the current boolean value and an object with three callbacks:
 * - `on`: Sets the state to `true`.
 * - `off`: Sets the state to `false`.
 * - `toggle`: Toggles the state between `true` and `false`.
 *
 * @example
 * const [isActive, { on, off, toggle }] = useBoolean();
 */
export const useBoolean = (initialState: InitialBooleanState = false) => {
  const [value, setValue] = useState<boolean>(initialState);
  const callbacks = useMemo(() => {
    return {
      off: () => {
        setValue(false);
      },
      on: () => {
        setValue(true);
      },
      toggle: () => {
        setValue((current) => {
          return !current;
        });
      },
    };
  }, []);

  return [value, callbacks] as const;
};
